import React, { useEffect, useCallback, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../../components/ErrorBoundary';
import TopNav from './components/TopNav';
import IconSidebar from './components/IconSidebar';
import SectionTransition from './components/SectionTransition';
import GlobalSearch from './components/GlobalSearch';
import { useTheme } from '../../context/ThemeContext';
import { TaskProvider } from '../../context/TaskContext';
import { useAnnouncements } from '../../hooks/use-api-announcements';
import { countUnreadAnnouncements } from '@/lib/adapters/announcements';
import { OnboardingTour } from '../../components/OnboardingTour';

// Lazy-loaded section components
const Overview = React.lazy(() => import('./components/Overview'));
const Xero = React.lazy(() => import('./components/Xero'));
const Analytics = React.lazy(() => import('./components/Analytics'));
const Integrations = React.lazy(() => import('./components/Integrations'));
const Reports = React.lazy(() => import('./components/Reports'));
const Communications = React.lazy(() => import('./components/Communications'));
const Tasks = React.lazy(() => import('./components/Tasks'));
const Settings = React.lazy(() => import('./components/Settings'));
const ActivityFeed = React.lazy(() => import('./components/ActivityFeed'));
const Triage = React.lazy(() => import('./components/Triage'));
const ClientPortal = React.lazy(() => import('./components/ClientPortal'));
const ClientHealth = React.lazy(() => import('./components/ClientHealth'));
const RecurringTemplates = React.lazy(() => import('./components/RecurringTemplates'));
const WorkflowBuilder = React.lazy(() => import('./components/WorkflowBuilder'));
const StaffWorkload = React.lazy(() => import('./components/StaffWorkload'));
const TimeTracking = React.lazy(() => import('./components/TimeTracking'));
const DocumentVault = React.lazy(() => import('./components/DocumentVault'));
const DeadlineCalendar = React.lazy(() => import('./components/DeadlineCalendar'));
const ComplianceTracker = React.lazy(() => import('./components/ComplianceTracker'));
const QuickNotes = React.lazy(() => import('./components/QuickNotes'));
const StaffOnboarding = React.lazy(() => import('./components/StaffOnboarding'));
const TaskDependencies = React.lazy(() => import('./components/TaskDependencies'));
const Announcements = React.lazy(() => import('./components/Announcements'));
const KanbanBoard = React.lazy(() => import('./components/KanbanBoard'));

const SectionLoadingFallback = () => {
  const { isDark } = useTheme();
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-3">
        <div
          className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${
            isDark ? 'border-teal-400' : 'border-teal-500'
          }`}
        />
        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Loading section…
        </span>
      </div>
    </div>
  );
};

const SECTION_TITLES: Record<string, string> = {
  'overview': 'Dashboard',
  'tasks': 'Tasks',
  'kanban': 'Kanban Board',
  'triage': 'Triage',
  'calendar': 'Calendar',
  'compliance': 'Compliance',
  'client-portal': 'Client Portal',
  'communications': 'Messages',
  'documents': 'Documents',
  'time-tracking': 'Time Tracking',
  'recurring': 'Recurring Tasks',
  'workflows': 'Workflows',
  'staff-workload': 'Staff Workload',
  'staff-onboarding': 'Staff Onboarding',
  'task-dependencies': 'Dependencies',
  'notes': 'Quick Notes',
  'announcements': 'Announcements',
  'analytics': 'Analytics',
  'reports': 'Reports',
  'integrations': 'Integrations',
  'xero': 'Xero',
  'settings': 'Settings',
  'client-health': 'Client Health',
  'activity': 'Activity Feed',
};

const VALID_SECTIONS = new Set(Object.keys(SECTION_TITLES));

export default function Home() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const activeSection = section && VALID_SECTIONS.has(section) ? section : 'overview';
  const [searchOpen, setSearchOpen] = React.useState(false);
  const announcementsQuery = useAnnouncements();
  // 0 hides the badge while the request is loading or has failed.
  const announcementsCount = countUnreadAnnouncements(announcementsQuery);
  const { isDark } = useTheme();

  const setActiveSection = useCallback((newSection: string) => {
    const search = window.location.search;
    if (newSection === 'overview') {
      navigate('/' + search);
    } else {
      navigate('/' + newSection + search);
    }
  }, [navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const sectionTitle = SECTION_TITLES[activeSection] || 'Dashboard';
    document.title = sectionTitle + ' | ABSee';
  }, [activeSection]);
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <Overview />;
      case 'tasks':
        return <Tasks />;
      case 'kanban':
        return <KanbanBoard />;
      case 'triage':
        return <Triage />;
      case 'calendar':
        return <DeadlineCalendar />;
      case 'compliance':
        return <ComplianceTracker />;
      case 'client-portal':
        return <ClientPortal />;
      case 'communications':
        return <Communications />;
      case 'documents':
        return <DocumentVault />;
      case 'time-tracking':
        return <TimeTracking />;
      case 'recurring':
        return <RecurringTemplates />;
      case 'workflows':
        return <WorkflowBuilder />;
      case 'staff-workload':
        return <StaffWorkload />;
      case 'staff-onboarding':
        return <StaffOnboarding />;
      case 'task-dependencies':
        return <TaskDependencies />;
      case 'notes':
        return <QuickNotes />;
      case 'announcements':
        return <Announcements />;
      case 'analytics':
        return <Analytics />;
      case 'reports':
        return <Reports />;
      case 'integrations':
        return <Integrations />;
      case 'xero':
        return <Xero />;
      case 'client-health':
        return <ClientHealth />;
      case 'activity':
        return <ActivityFeed />;
      case 'settings':
        return <Settings />;
      default:
        return <Overview />;
    }
  };

  return (
    <TaskProvider>
      <div className={`flex flex-col h-screen overflow-x-hidden font-['Inter',sans-serif] transition-colors duration-300 ${isDark ? 'bg-[#0D0F14]' : 'bg-[#F5F6FA]'}`}>
        <TopNav
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onSearchOpen={() => setSearchOpen(true)}
        />
        <div className="flex flex-1 overflow-hidden min-w-0">
          <IconSidebar 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            announcementsCount={announcementsCount}
          />
          <main data-tour="dashboard" className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
            <SectionTransition sectionKey={activeSection}>
              <ErrorBoundary key={activeSection} sectionName={activeSection}>
                <Suspense fallback={<SectionLoadingFallback />}>
                  {renderContent()}
                </Suspense>
              </ErrorBoundary>
            </SectionTransition>
          </main>
        </div>

        <GlobalSearch
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
          setActiveSection={(section) => {
            setActiveSection(section);
            setSearchOpen(false);
          }}
        />
        <OnboardingTour />
      </div>
    </TaskProvider>
  );
}